import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Button} from "primereact/button";
import {Card} from "primereact/card";
import {InputSwitch} from "primereact/inputswitch";
import {InputNumber} from "primereact/inputnumber";
import {Message} from "primereact/message";
import {ProgressSpinner} from "primereact/progressspinner";
import {TabPanel, TabView} from "primereact/tabview";
import {Tag} from "primereact/tag";
import PageHeader from "../components/page-header";
import {useTranslate} from "../../system/useTranslate";
import {useAi} from "../../models/ai/use-ai";
import {useWorkspaceRole} from "../../system/use-workspace-role";
import {normalizePermissionList} from "../../system/workspace-permissions";
import Icon from "../../system/components/icon";

const confidencePercent = (value) => `${Math.round(Number(value || 0) * 100)}%`;
const formatDate = (value) => value ? new Date(value).toLocaleDateString() : "—";

function Empty({label}) {
    return <div className="surface-50 border-round p-4 text-center text-500 text-xs">{label}</div>;
}

function Evidence({value, translate}) {
    if (!value) return null;
    const source = Array.isArray(value) ? {items: value} : value;
    const items = source.topTasks || source.tasks || source.items || source.quickWins || [];
    const visibleStats = new Set([
        "count", "ratio", "peakAssignedOpen", "totalTasks", "openTasks", "completedTasks",
        "overdueTasks", "blockedTasks", "dependencyRisk", "completionRatio", "schedulePressure",
    ]);
    const stats = Object.entries(source).filter(([key, entry]) => (
        visibleStats.has(key) && ["string", "number"].includes(typeof entry)
    ));
    if (!stats.length && !items.length) return null;

    return (
        <div className="surface-50 border-round p-3 mt-3 flex flex-column gap-2">
            <span className="font-semibold text-xs">{translate("ai.details.evidence")}</span>
            {stats.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {stats.map(([key, entry]) => (
                        <Tag key={key} severity="info" value={`${translate(`ai.evidence.${key}`)}: ${typeof entry === "number" && entry <= 1 ? confidencePercent(entry) : entry}`}/>
                    ))}
                </div>
            )}
            {items.slice(0, 6).map((item, index) => (
                <div key={item.documentId || `${item.title}-${index}`} className="flex justify-content-between align-items-start gap-2 border-top-1 border-200 pt-2">
                    <div className="flex flex-column gap-1">
                        <span className="text-xs text-800">{item.title || item.name || item.projectTitle || item.documentId}</span>
                        {item.projectTitle && item.title && <span className="text-xs text-500">{item.projectTitle}</span>}
                    </div>
                    <div className="flex gap-2 align-items-center">
                        {item.daysOverdue > 0 && <Tag severity="danger" value={`${item.daysOverdue} ${translate("ai.details.days")}`}/>}
                        {item.score !== undefined && <Tag severity="success" value={`${translate("ai.details.score")}: ${item.score}`}/>}
                        {item.count !== undefined && <Tag severity="warning" value={`${translate("ai.evidence.openTasks")}: ${item.count}`}/>}
                        {item.taskStatus && <Tag value={translate(`ai.task-status.${item.taskStatus}`)}/>}
                    </div>
                </div>
            ))}
        </div>
    );
}

function Scenario({label, value, translate}) {
    return (
        <div className="surface-50 border-round p-3 flex flex-column gap-2">
            <span className="font-semibold text-xs">{label}</span>
            <span className="text-xs text-600">{translate("ai.details.completionDate")}: {formatDate(value?.completionDate)}</span>
            <span className="text-xs text-600">{translate("ai.details.delayDays")}: {value?.delayDays ?? "—"}</span>
        </div>
    );
}

function InsightCard({item, translate}) {
    return (
        <Card className="border-1 border-200 shadow-none">
            <div className="flex justify-content-between align-items-start gap-2">
                <div className="flex flex-column gap-2">
                    <span className="font-bold text-xs">{translate(item.title)}</span>
                    <span className="text-xs text-600 line-height-3 white-space-pre-wrap">{translate(item.summary)}</span>
                </div>
                <Tag severity={item.severity === "critical" ? "danger" : item.severity === "warning" ? "warning" : "info"} value={translate(`ai.severity.${item.severity}`)}/>
            </div>
            <Evidence value={item.evidence} translate={translate}/>
            <div className="text-xs text-500 mt-3">{translate("ai.confidence")}: {confidencePercent(item.confidence)}</div>
        </Card>
    );
}

function AiCommandCenter(props) {
    const translate = useTranslate();
    const {ai} = useAi();
    const {workspaceRole} = useWorkspaceRole();
    const permissions = useMemo(() => new Set(normalizePermissionList(workspaceRole?.permissions)), [workspaceRole]);
    const canManage = workspaceRole?.role === "owner" || permissions.has("*") || permissions.has("manage-ai-settings");
    const canApprove = workspaceRole?.role === "owner" || permissions.has("*") || permissions.has("approve-ai-actions");
    const [data, setData] = useState(null);
    const [usage, setUsage] = useState(null);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [center, usageData, settingsData] = await Promise.all([
                ai.commandCenter(),
                ai.usage(),
                canManage ? ai.settings() : Promise.resolve(null),
            ]);
            setData(center);
            setUsage(usageData);
            setSettings(settingsData);
        } catch (requestError) {
            setError(requestError);
        } finally {
            setLoading(false);
        }
    }, [ai, canManage]);

    useEffect(() => { load(); }, [load]);

    const runAnalysis = async () => {
        setWorking(true);
        setError(null);
        try {
            await ai.runAnalysis();
            await load();
        } catch (requestError) {
            setError(requestError);
        } finally {
            setWorking(false);
        }
    };

    const recommendationDecision = async (item, decision) => {
        setWorking(true);
        try {
            await ai.decideRecommendation(item.documentId, {decision});
            await load();
        } catch (requestError) {
            setError(requestError);
        } finally {
            setWorking(false);
        }
    };

    const proposalAction = async (proposal, action) => {
        setWorking(true);
        setError(null);
        try {
            if (action === "validate") await ai.validateProposal(proposal.documentId);
            if (action === "submit") await ai.submitProposal(proposal.documentId);
            if (action === "approve") await ai.approveProposal(proposal.documentId);
            if (action === "reject") await ai.rejectProposal(proposal.documentId);
            if (action === "execute") await ai.executeProposal(proposal.documentId, `${proposal.documentId}-${proposal.version}`);
            await load();
        } catch (requestError) {
            setError(requestError);
        } finally {
            setWorking(false);
        }
    };

    const saveSettings = async () => {
        setWorking(true);
        try {
            const updated = await ai.updateSettings(settings.setting);
            setSettings(updated);
        } catch (requestError) {
            setError(requestError);
        } finally {
            setWorking(false);
        }
    };

    if (loading) return <div className="h-full flex align-items-center justify-content-center"><ProgressSpinner/></div>;

    return (
        <div className="h-full min-h-0 overflow-hidden flex flex-column gap-3 p-3">
            <PageHeader title={props.name || "ai.title"} description={props.description || "ai.description"}/>
            {error && <Message severity="error" text={translate(error.code || "ai.error.generic")}/>}
            <div className="flex flex-wrap gap-2 justify-content-end">
                <Button className="text-xs gap-2" icon="pi pi-refresh" outlined label={translate("ai.refresh")} disabled={working} onClick={load}/>
                <Button className="text-xs gap-2" icon="pi pi-chart-line" label={translate("ai.analysis.run")} loading={working} disabled={workspaceRole?.role === "guest"} onClick={runAnalysis}/>
            </div>
            <div className="grid flex-none">
                {["insightCount", "forecastCount", "recommendationCount", "pendingProposalCount"].map((key) => (
                    <div className="col-6 lg:col-3" key={key}>
                        <Card className="shadow-none border-1 border-200 text-center">
                            <div className="text-2xl font-bold text-primary">{data?.managementBrief?.[key] || 0}</div>
                            <div className="text-xs text-500 mt-2">{translate(`ai.brief.${key}`)}</div>
                        </Card>
                    </div>
                ))}
            </div>
            <div className="flex-1 min-h-0 flex flex-column overflow-hidden">
            <TabView
                className="h-full min-h-0 flex flex-column text-xs"
                panelContainerClassName="flex-1 min-h-0 overflow-auto p-3 border-noround"
            >
                <TabPanel header={translate("ai.tabs.insights")} leftIcon={<Icon icon="mi mi:lightbulb" className="pl-2"/>} className="text-xs">
                    <div className="flex flex-column gap-2">
                        {data?.insights?.length ? data.insights.map((item) => <InsightCard key={item.documentId || item.id} item={item} translate={translate}/>) : <Empty label={translate("ai.empty.insights")}/>}
                    </div>
                </TabPanel>
                <TabPanel header={translate("ai.tabs.forecasts")} leftIcon={<Icon icon="mi mi:query_stats" className="pl-2"/>} className="text-xs">
                    <div className="flex flex-column gap-2">
                        {data?.forecasts?.length ? data.forecasts.map((item) => (
                            <Card key={item.documentId || item.id} className="shadow-none border-1 border-200">
                                <div className="flex justify-content-between align-items-start gap-2">
                                    <div className="flex flex-column gap-1">
                                        <span className="font-bold text-xs">{translate(`ai.forecast.${item.forecastType}`)}</span>
                                        {item.drivers?.projectTitle && <span className="text-xs text-600">{item.drivers.projectTitle}</span>}
                                    </div>
                                    <Tag value={`${translate("ai.probability")}: ${confidencePercent(item.probability)}`}/>
                                </div>
                                <div className="grid mt-2">
                                    <div className="col-12 md:col-4"><Scenario label={translate("ai.details.optimistic")} value={item.optimistic} translate={translate}/></div>
                                    <div className="col-12 md:col-4"><Scenario label={translate("ai.details.likely")} value={item.likely} translate={translate}/></div>
                                    <div className="col-12 md:col-4"><Scenario label={translate("ai.details.pessimistic")} value={item.pessimistic} translate={translate}/></div>
                                </div>
                                <Evidence value={{...item.drivers, tasks: Array.isArray(item.evidence) ? item.evidence : item.evidence?.tasks}} translate={translate}/>
                                <div className="text-xs text-500 mt-3">{translate("ai.confidence")}: {confidencePercent(item.confidence)}</div>
                            </Card>
                        )) : <Empty label={translate("ai.empty.forecasts")}/>}
                    </div>
                </TabPanel>
                <TabPanel header={translate("ai.tabs.recommendations")} leftIcon={<Icon icon="mi mi:recommend" className="pl-2"/>} className="text-xs">
                    <div className="flex flex-column gap-2">
                        {data?.recommendations?.length ? data.recommendations.map((item) => (
                            <Card key={item.documentId || item.id} className="shadow-none border-1 border-200">
                                <div className="font-bold text-xs">{translate(item.title)}</div>
                                <p className="text-xs text-600 line-height-3">{translate(item.summary)}</p>
                                {item.reasoning && (
                                    <div className="surface-50 border-round p-3 text-xs text-700 line-height-3">
                                        <span className="font-semibold">{translate("ai.details.reasoning")}: </span>
                                        {translate(item.reasoning)}
                                    </div>
                                )}
                                <Evidence value={item.evidence} translate={translate}/>
                                <div className="flex flex-wrap gap-2">
                                    <Button className="text-xs gap-2" size="small" icon="pi pi-check" label={translate("ai.accept")} disabled={working} onClick={() => recommendationDecision(item, "accept")}/>
                                    <Button className="text-xs gap-2" size="small" icon="pi pi-refresh" outlined label={translate("ai.revision")} disabled={working} onClick={() => recommendationDecision(item, "request_revision")}/>
                                    <Button className="text-xs gap-2" size="small" icon="pi pi-times" text severity="danger" label={translate("ai.reject")} disabled={working} onClick={() => recommendationDecision(item, "reject")}/>
                                </div>
                            </Card>
                        )) : <Empty label={translate("ai.empty.recommendations")}/>}
                    </div>
                </TabPanel>
                <TabPanel header={translate("ai.tabs.proposals")} leftIcon={<Icon icon="mi mi:task_alt" className="pl-2"/>} className="text-xs">
                    <div className="flex flex-column gap-2">
                        {data?.proposals?.length ? data.proposals.map((proposal) => (
                            <Card key={proposal.documentId || proposal.id} className="shadow-none border-1 border-200">
                                <div className="flex justify-content-between gap-2">
                                    <span className="font-bold text-xs">{proposal.title}</span>
                                    <Tag value={translate(`ai.proposal.${proposal.proposalStatus}`)}/>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-3">
                                    {proposal.proposalStatus === "draft" && <Button className="text-xs gap-2" icon="pi pi-check-circle" size="small" outlined label={translate("ai.validate")} onClick={() => proposalAction(proposal, "validate")}/>}
                                    {proposal.proposalStatus === "ready" && <Button className="text-xs gap-2" icon="pi pi-send" size="small" label={translate("ai.submit")} onClick={() => proposalAction(proposal, "submit")}/>}
                                    {canApprove && ["submitted", "partially_approved"].includes(proposal.proposalStatus) && <>
                                        <Button className="text-xs gap-2" icon="pi pi-check" size="small" label={translate("ai.approve")} onClick={() => proposalAction(proposal, "approve")}/>
                                        <Button className="text-xs gap-2" icon="pi pi-times" size="small" severity="danger" outlined label={translate("ai.reject")} onClick={() => proposalAction(proposal, "reject")}/>
                                    </>}
                                    {canApprove && proposal.proposalStatus === "approved" && <Button className="text-xs gap-2" icon="pi pi-play" size="small" severity="success" label={translate("ai.execute")} onClick={() => proposalAction(proposal, "execute")}/>}
                                </div>
                            </Card>
                        )) : <Empty label={translate("ai.empty.proposals")}/>}
                    </div>
                </TabPanel>
                <TabPanel header={translate("ai.tabs.usage")} leftIcon={<Icon icon="mi mi:data_usage" className="pl-2"/>} className="text-xs">
                    <div className="grid">
                        {["requestCount", "totalTokens", "chargedCredits", "providerCostMicros"].map((key) => (
                            <div className="col-6 lg:col-3" key={key}><Card className="shadow-none border-1 border-200 text-center">
                                <div className="text-xl font-bold">{usage?.summary?.[key] || "0"}</div>
                                <span className="text-xs text-500">{translate(`ai.usage.${key}`)}</span>
                            </Card></div>
                        ))}
                    </div>
                </TabPanel>
                {canManage && settings?.setting && (
                    <TabPanel header={translate("ai.tabs.settings")} leftIcon={<Icon icon="mi mi:settings" className="pl-2"/>} className="text-xs">
                        <div className="flex flex-column gap-3 max-w-30rem">
                            {["aiEnabled", "allowFallback", "allowAutoDowngrade"].map((key) => (
                                <label key={key} className="flex justify-content-between align-items-center gap-3">
                                    <span className="text-xs">{translate(`ai.settings.${key}`)}</span>
                                    <InputSwitch checked={Boolean(settings.setting[key])} onChange={(event) => setSettings((current) => ({...current, setting: {...current.setting, [key]: event.value}}))}/>
                                </label>
                            ))}
                            {["dailyCreditLimit", "monthlyCreditLimit", "retentionDays"].map((key) => (
                                <label key={key} className="flex justify-content-between align-items-center gap-3">
                                    <span className="text-xs">{translate(`ai.settings.${key}`)}</span>
                                    <InputNumber value={Number(settings.setting[key] || 0)} onValueChange={(event) => setSettings((current) => ({...current, setting: {...current.setting, [key]: event.value}}))}/>
                                </label>
                            ))}
                            <Button className="text-xs gap-2" icon="pi pi-save" label={translate("ai.settings.save")} loading={working} onClick={saveSettings}/>
                        </div>
                    </TabPanel>
                )}
            </TabView>
            </div>
        </div>
    );
}

export default AiCommandCenter;
